import { useState, useEffect, useCallback } from "react";
import "../Components/datagrid-override.css";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Button,
  IconButton,
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupIcon from "@mui/icons-material/Group";
import ReusableFormDialog from "./FormModal/ReusableFormDialog";
import DeleteConfirmationDialog from "./FormModal/DeleteConfirmationDialog";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import { roleValidationSchema } from "./FormModal/validationSchemas";

function Roles() {
  const current_url = useLocation();
  const navigate = useNavigate();

  // Initial form values - DEFINE THESE FIRST
  const roleInit = {
    name: "",
    user_id: Helpers.authParentId,
  };

  const [search, setSearch] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [roles, setRoles] = useState([]);
  const [currentItem, setCurrentItem] = useState(roleInit);
  const [perms, setPerms] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(true);

  // Form fields
  const roleFields = [{ name: "name", label: "Role Name", type: "text" }];

  // Role columns
  const roleColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: "name",
      headerName: "Role Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "managePermissions",
      headerName: "Manage Permissions",
      flex: 1,
      minWidth: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Button
            component={Link}
            to={`/user/manage-permissions/${params.row.name}/${params.row._id}`}
            variant="contained"
            size="small"
            startIcon={<SettingsIcon fontSize="small" />}
            sx={{
              textTransform: "none",
              backgroundColor: "#ff9800",
              "&:hover": {
                backgroundColor: "#f57c00",
              },
            }}
          >
            Manage Permissions
          </Button>
        </Box>
      ),
    },
  ];

  // Fetch data
  const getRoles = useCallback(() => {
    setLoadingRoles(true);
    axios
      .get(
        `${Helpers.baseUrl}/roles/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        setRoles(response.data.reverse());
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoadingRoles(false);
      });
  }, [navigate]);

  // Search filtering
  const filteredRoles = roles.filter((role) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (role.name || "").toLowerCase().includes(term);
  });

  // CRUD operations
  const handleSave = (values) => {
    setBtnLoading(true);
    const addOrUpdate = isEditing ? "update" : "add";
    axios
      .post(
        `${Helpers.baseUrl}/roles/${addOrUpdate}`,
        values,
        Helpers.headers,
      )
      .then((response) => {
        getRoles();
        setCurrentItem(roleInit);
        setIsEditing(false);
        setOpenFormDialog(false);
        Helpers.toast("success", "Role saved successfully");
        setBtnLoading(false);
      })
      .catch((error) => {
        Helpers.toast("error", Helpers.error_response(error));
        setBtnLoading(false);
      });
  };

  const handleEdit = (item) => {
    setIsEditing(true);
    setCurrentItem(item);
    setOpenFormDialog(true);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    const initialValues = { ...roleInit };
    setCurrentItem(initialValues);
    setOpenFormDialog(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);

    axios
      .get(
        `${Helpers.baseUrl}/roles/delete/${itemToDelete._id}`,
        Helpers.headers,
      )
      .then((response) => {
        getRoles();
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        setDeleteLoading(false);
        Helpers.toast("success", "Role deleted successfully");
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        setDeleteLoading(false);
      });
  };

  const getPerms = useCallback(() => {
    const link = current_url.pathname;
    const permissions = JSON.parse(localStorage.getItem("permissions"));
    if (permissions) {
      const perm = permissions.find(
        (permission) => permission.tab_link === link,
      );
      setPerms(perm || {});
    }
  }, [current_url.pathname]);

  useEffect(() => {
    getRoles();
    getPerms();
  }, [getPerms, getRoles]);

  const canCreate =
    Helpers.authUser.user_role == null || perms.can_create === 1;
  const canUpdate =
    Helpers.authUser.user_role == null || perms.can_update === 1;
  const canDelete =
    Helpers.authUser.user_role == null || perms.can_delete === 1;

  // Actions renderer
  const actionsRenderer = (params) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        gap: 0.5,
      }}
    >
      {canUpdate && (
        <IconButton
          onClick={() => handleEdit(params.row)}
          color="success"
          size="small"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {canDelete && (
        <IconButton
          onClick={() => handleDelete(params.row)}
          color="error"
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // Enhanced columns with actions
  const enhancedRoleColumns = [
    ...roleColumns,
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: actionsRenderer,
    },
  ];

  // Prepare rows
  const roleRows = filteredRoles.map((role, index) => ({
    ...role,
    id: role._id,
    serial: index + 1,
  }));

  const breadcrumbs = [
    {
      label: "Home",
      path: "/user/dashboard",
      icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
    },
    { label: "Roles", path: null },
  ];

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: 3,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "300px",
              height: "300px",
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon
                fontSize="small"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              />
            }
            sx={{ mb: 2 }}
          >
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return isLast ? (
                <Typography
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {item.icon}
                  {item.label}
                </Typography>
              ) : (
                <MuiLink
                  key={index}
                  component={Link}
                  to={item.path}
                  underline="hover"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "rgba(255,255,255,0.9)",
                    fontSize: "0.875rem",
                    "&:hover": { color: "white" },
                  }}
                >
                  {item.icon}
                  {item.label}
                </MuiLink>
              );
            })}
          </Breadcrumbs>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 0.5,
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                Roles
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
              >
                Manage user roles and permissions
              </Typography>
            </Box>
            <Chip
              icon={<GroupIcon sx={{ color: "white !important" }} />}
              label={`${filteredRoles.length} Roles`}
              size="small"
              sx={{
                background: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            />
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
            mb: 5.25,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
              flexShrink: 0,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                All Roles
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ minWidth: { sm: "auto", md: "50%" } }}
              >
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "200px" } }}
                />
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddNew}
                    size="small"
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      backgroundColor: "#28a745",
                      "&:hover": { backgroundColor: "#218838" },
                    }}
                  >
                    Add Role
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <ReusableDataGrid
              rows={roleRows}
              loading={loadingRoles}
              columns={enhancedRoleColumns}
              pageSize={10}
              pageSizeOptions={[10, 25, 50, 100]}
              noRowsMessage="No roles available to display"
              autoHeight={false}
              sx={{
                flex: 1,
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.04)",
                },
              }}
            />
          </Box>
        </Paper>
      </Container>

      <ReusableFormDialog
        open={openFormDialog}
        onClose={() => {
          setOpenFormDialog(false);
          setCurrentItem(roleInit);
          setIsEditing(false);
        }}
        title={
          isEditing ? `Edit Role: ${currentItem.name || ""}` : "Add New Role"
        }
        initialValues={currentItem}
        validationSchema={roleValidationSchema}
        fields={roleFields}
        onSubmit={handleSave}
        loading={btnLoading}
      />

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
        title="Delete Role"
        itemName={itemToDelete?.name}
        itemType="Role"
        warningText="This action cannot be undone. All team members assigned to this role will need to be reassigned to a different role."
        loading={deleteLoading}
      />
    </Box>
  );
}

export default Roles;
